import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarDays, ArrowRight } from "lucide-react";
import { Seo } from "@/seo/Seo";
import { breadcrumbSchema } from "@/seo/schemas";

import { articlesApi, type ArticleSummary } from "@/api/articlesApi";
import { Button } from "@/components/ui/button";

export default function Knowledge() {
  const [articles, setArticles] = useState<ArticleSummary[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page") || "1") || 1;
  // 12 fills the 2-/3-column grid evenly (no lone last card).
  const pageSize = 12;

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    articlesApi
      .getAll(page, pageSize)
      .then((res) => {
        if (cancelled) return;

        const data = res.data;

        setArticles(data.results || []);
        setCount(data.count || 0);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Failed to load articles. Please try again later.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return;

    setSearchParams({ page: String(nextPage) });
  };

  return (
    <>
      <Seo
        title={page > 1 ? `Physiotherapy Guides - Page ${page} | Recovry` : "Physiotherapy Guides & Recovery Advice | Recovry"}
        description="Clinician-reviewed guides on back pain, knee pain, sciatica, sports injuries, post-surgery rehab and mobility, and when to see a physiotherapist."
        canonical={`/knowledge${page > 1 ? `?page=${page}` : ""}`}
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Physiotherapy Guides", path: "/knowledge" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Recovry Physiotherapy Guides",
            description: "Clinician-reviewed guides on pain, injury recovery and mobility.",
            url: "https://www.recovry.in/knowledge",
            publisher: {
              "@type": "Organization",
              name: "Recovry",
              logo: { "@type": "ImageObject", url: "https://www.recovry.in/recovry-wordmark.png" },
            },
          },
        ]}
      />

      <div className="mx-auto max-w-[1440px] px-5 py-10 sm:px-8 lg:px-12">
        <header className="mb-8 space-y-2 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Physiotherapy Guides & Recovery Advice
          </h1>

          <p className="text-sm text-muted-foreground">
            Clinician-reviewed guides on back and knee pain, sciatica, sports injuries,
            post-surgery rehab and staying mobile - and when to see a physiotherapist.
          </p>
        </header>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl border bg-card p-4"
              >
                <div className="h-5 w-2/3 rounded bg-muted" />
                <div className="mt-2 h-4 w-full rounded bg-muted" />
                <div className="mt-1 h-4 w-4/5 rounded bg-muted" />
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <p className="text-center text-sm text-red-500">{error}</p>
        )}

        {/* Empty state */}
        {!loading && !error && articles.length === 0 && (
          <p className="text-center text-sm text-muted-foreground">
            Articles coming soon. Our team is preparing helpful guides for you.
          </p>
        )}

        {/* Article List */}
        {!loading && !error && articles.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((article) => (
              <article
                key={article.slug}
                itemScope
                itemType="https://schema.org/BlogPosting"
                className="group relative flex h-full cursor-pointer flex-col rounded-xl border bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
              >
                {/* Stretched link: the whole card is clickable, but there's still
                    only one real link for a11y/SEO (its ::after covers the card). */}
                <Link
                  to={`/knowledge/${article.slug}`}
                  aria-label={`Read article ${article.title}`}
                  className="after:absolute after:inset-0"
                >
                  <h2
                    itemProp="headline"
                    className="text-lg font-semibold text-foreground transition-colors group-hover:text-primary"
                  >
                    {article.title}
                  </h2>
                </Link>

                {article.published_date && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <CalendarDays className="size-3.5" />

                    <span itemProp="datePublished">
                      {article.published_date}
                    </span>
                  </div>
                )}

                {article.summary && (
                  <p
                    itemProp="description"
                    className="mt-2 text-sm text-muted-foreground line-clamp-3"
                  >
                    {article.summary.replace(/\s*\u2014\s*/g, ', ')}
                  </p>
                )}

                {/* Visual "Read more" cue only \u2014 not a separate link; the card's
                    stretched link handles the click. mt-auto keeps it pinned to
                    the bottom so cards stay evenly spaced (not compacted). */}
                <div className="mt-auto pt-4">
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-primary group-hover:underline">
                    Read more
                    <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              Previous
            </Button>

            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>

            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => handlePageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </>
  );
}